import React, { useState, useRef } from 'react';
import Button from '../Button';
import ReCaptcha from '../Contact/Recaptcha';
import { createUrl } from '../../utils.js';
import { useTranslation } from 'react-i18next';

const ContactForm = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    message: '',
    token: '',
  });

  const [emailError, setEmailError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false);

  const recaptchaRef = useRef(null);

  const disallowedDomains = [
    'gmail.com',
    'hotmail.com',
    'hotmail.co.uk',
    'yahoo.com',
    'aol.com',
    'icloud.com',
    'mail.com',
    'outlook.com',
    'zoho.com',
    'gmx.com',
    'inbox.com',
    'yandex.com',
    'protonmail.com',
    'tutanota.com',
    'fastmail.com',
    'hushmail.com',
    'sendinblue.com',
    'mailinator.com',
    'mail.ru',
    'rediffmail.com',
  ];

  const resetMessage = () => {
    setTimeout(() => {
      setIsSubmitted(false);
      setIsFailed(false);
    }, 15000);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handlePrivacyPolicyChange = (e) => {
    setPrivacyPolicyChecked(e.target.checked);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return t('contact.contact_form.email_error');
    }

    const domain = email.split('@')[1];
    if (disallowedDomains.includes(domain)) {
      return t('contact.contact_form.email_error');
    }

    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    setEmailError('');

    try {
      const endpoint = createUrl('/send-email');
      console.log('POST TO ' + endpoint);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setIsFailed(false);

        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          message: '',
          token: '',
        });

        setPrivacyPolicyChecked(false);
        recaptchaRef.current.resetRecaptcha();
        resetMessage();
      } else {
        setIsSubmitted(false);
        setIsFailed(true);
        resetMessage();
      }
    } catch (error) {
      console.error('Error:', error);
      setIsSubmitted(false);
      setIsFailed(true);
      resetMessage();
    }
  };

  const handleVerify = (newToken) => {
    setFormData((prevData) => ({ ...prevData, token: newToken }));
  };

  return (
    <div className='flex flex-col w-full lg:w-1/2 lg:order-2 order-1'>
      <form onSubmit={handleSubmit}>
        <div className='sm:grid-cols-2 grid grid-cols-1 gap-x-8 gap-y-8'>
          {['firstName', 'lastName'].map((field) => (
            <div key={field}>
              <label
                htmlFor={field}
                className='block text-lg lg:text-xl text-primary-blue'
              >
                {t(`contact.contact_form.${field}.label`)}
              </label>
              <input
                id={field}
                placeholder={t(`contact.contact_form.${field}.placeholder`)}
                type='text'
                value={formData[field]}
                onChange={handleInputChange}
                className='block w-full rounded border-0 px-3.5 py-2 text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-blue sm:text-sm sm:leading-8'
              />
            </div>
          ))}
          <div className='sm:col-span-2'>
            <label
              htmlFor='email'
              className='block text-lg lg:text-xl text-primary-blue'
            >
              {t('contact.contact_form.email.label')}
            </label>
            <input
              id='email'
              placeholder={t('contact.contact_form.email.placeholder')}
              type='email'
              value={formData.email}
              onChange={handleInputChange}
              className={`block w-full rounded border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-blue sm:text-sm sm:leading-6 ${
                emailError ? 'border-red-500 ring-red-500' : ''
              }`}
            />
            {emailError && (
              <p className='text-primary-red text-sm'>{emailError}</p>
            )}
          </div>
          <div className='sm:col-span-2'>
            <label
              htmlFor='message'
              className='block text-lg lg:text-xl text-primary-blue'
            >
              {t('contact.contact_form.message.label')}
            </label>
            <textarea
              id='message'
              placeholder={t('contact.contact_form.message.placeholder')}
              rows={4}
              value={formData.message}
              onChange={handleInputChange}
              className='block w-full rounded border-0 px-3.5 py-2 text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-blue sm:text-sm sm:leading-6'
            />
          </div>
          <div className='flex items-center sm:col-span-2 gap-2'>
            <input
              id='privacy-policy'
              type='checkbox'
              value={privacyPolicyChecked}
              className='h-4 w-4 text-primary-red border border-gray-300 rounded'
              checked={privacyPolicyChecked}
              onChange={handlePrivacyPolicyChange}
              required
            />
            <label
              htmlFor='privacy-policy'
              className='block text-sm text-gray-500'
            >
              {t('contact.contact_form.privacy.label')}{' '}
              <a
                target='_blank'
                rel='noopener noreferrer'
                href='/privacypolicy'
                className='font-semibold text-primary-red'
              >
                {t('contact.contact_form.privacy.linkText')}
              </a>
              .
            </label>
          </div>
        </div>
        <ReCaptcha ref={recaptchaRef} onVerify={handleVerify} />
        <div className='flex justify-start items-center mt-12'>
          <Button className='w-48' type='submit'>
            {t('contact.contact_form.submit_button')}
          </Button>
        </div>
      </form>

      {isSubmitted && (
        <div className='text-green-500 mt-4'>
          {t('contact.contact_form.success_message')}
        </div>
      )}
      {isFailed && (
        <div className='text-red-500 mt-4'>
          {t('contact.contact_form.error_message')}
        </div>
      )}
    </div>
  );
};

export default ContactForm;
